import { DeleteOutlined, FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { Checkbox, Input, InputRef, Popover } from "antd";
import _ from "lodash";
import { useCallback, useRef, useState } from "react";
import SortIcon from "../icons/SortIcon";
import { AvailableFilters, Column, DataRecord, Filter, Search, Sort, SortOrder } from "./Table";
import styles from "./Table.module.scss";

export type OnSortFilterSearchChange = ({
  key,
  searchTerm,
  filters,
  sortOrder,
}: {
  key: string;
  searchTerm?: string | null;
  filters?: string[] | null;
  sortOrder?: SortOrder | null;
}) => void;

export default function ColumnHeader<R extends DataRecord>({
  column,
  onSortFilterSearchChange,
  availableFilters,
  activeSort,
  activeFilter,
  activeSearch,
}: {
  column: Column<R>;
  availableFilters: AvailableFilters;
  onSortFilterSearchChange: OnSortFilterSearchChange;
  activeSort: Sort | null;
  activeFilter: Filter;
  activeSearch: Search;
}) {
  const [searchVisible, setSearchVisible] = useState<boolean>(false);
  const [filterVisible, setFilterVisible] = useState<boolean>(false);
  const [localSearch, setLocalSearch] = useState<string | undefined>(activeSearch?.[column.key]);

  const sort = activeSort?.key === column.key ? activeSort.order : undefined;
  const search = activeSearch?.[column.key];
  const filters = activeFilter?.[column.key];

  const searchInput = useRef<InputRef>(null);

  const toggleSearch = () => {
    setSearchVisible(!searchVisible);
    setTimeout(() => searchInput.current?.focus(), 250);
  };

  const toggleFilter = () => {
    setFilterVisible(!filterVisible);
  };

  const clear = () => {
    onSortFilterSearchChange({
      key: column.key,
      searchTerm: null,
      filters: null,
      sortOrder: sort ? null : undefined,
    });
  };

  const toggleSort = () => {
    onSortFilterSearchChange({
      key: column.key,
      sortOrder: !sort ? "ascend" : sort === "ascend" ? "descend" : null,
    });
  };

  const saveSearch = useCallback(
    (searchTerm: string) => {
      onSortFilterSearchChange({
        key: column.key,
        searchTerm,
      });
    },
    [onSortFilterSearchChange]
  );

  const debouncedSetSearch = useCallback(_.debounce(saveSearch, 250), [saveSearch]);

  const setSearch = (search: string) => {
    setLocalSearch(search);
    debouncedSetSearch(search);
  };

  const setFilters = (filters: string[]) => {
    onSortFilterSearchChange({
      key: column.key,
      filters,
    });
  };

  return (
    <div
      className={styles.column}
      key={column.key}
    >
      <div className={[styles.wrapper, styles[column.align || "start"]].join(" ")}>
        {column.label}
        {(column.sortable || column.filterable || column.searchable) && (
          <span
            className={`${styles.actions} ${
              (search || filters || sort || searchVisible || filterVisible) && styles.open
            }`}
          >
            {column.sortable && (
              <span
                className={`${styles.sorting} ${sort && styles.active}`}
                onClick={toggleSort}
              >
                <SortIcon order={sort || undefined} />
              </span>
            )}
            {column.filterable && (
              <Popover
                open={filterVisible}
                onOpenChange={(visible) => setFilterVisible(visible)}
                trigger="click"
                placement="bottom"
                content={
                  availableFilters?.[column.key] && availableFilters?.[column.key].length ? (
                    <Checkbox.Group
                      className={styles["checkbox-group"]}
                      options={availableFilters?.[column.key]}
                      onChange={(checkedValue) =>
                        setFilters(checkedValue.length ? checkedValue : [])
                      }
                      value={filters || []}
                    />
                  ) : (
                    "No filters available"
                  )
                }
              >
                <span
                  className={`${styles.filter} ${(filters || filterVisible) && styles.active}`}
                  onClick={toggleFilter}
                >
                  <FilterOutlined />
                </span>
              </Popover>
            )}
            {column.searchable && (
              <Popover
                open={searchVisible}
                onOpenChange={(visible) => setSearchVisible(visible)}
                trigger="click"
                placement="bottom"
                content={
                  <Input
                    placeholder="Search..."
                    allowClear={true}
                    value={localSearch || ""}
                    onChange={(e) => setSearch(e.target.value)}
                    ref={searchInput}
                    onBlur={() => toggleSearch()}
                    style={{ width: 150 }}
                  />
                }
              >
                <span
                  className={`${styles.search} ${(search || searchVisible) && styles.active}`}
                  onClick={toggleSearch}
                >
                  <SearchOutlined />
                </span>
              </Popover>
            )}
            {(search || filters || sort) && (
              <span
                className={styles.clear}
                onClick={clear}
              >
                <DeleteOutlined />
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}
